import { useCallback } from 'react'

import { Checkbox } from 'core-components/library/checkbox'
import { ColorSelectorButton } from 'core-components/library/common'
import { Input } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'
import { IPageWidget, IStyleSettings, IWidget } from 'core-components/sitebuilder/models/IWidget'
import { BorderSelector } from 'core-components/sitebuilder/settings/detail/basicSettings/BorderSelector'
import { SizeSelector } from 'core-components/sitebuilder/settings/detail/basicSettings/SizeSelector'
import { TDetailState } from 'core-components/sitebuilder/settings/models/TState'

import { AdditionalSettings } from './customSettings/AdditionalSettings'
import { HeightSelector } from './customSettings/HeightSelector'
import { StaticPropsEditor } from './customSettings/StaticPropsEditor'

import styles from './CustomSettings.module.scss'

interface IProps {
  onChangeField: (field: string, value: any) => void
  updateState: (key: TDetailState, value: any) => void
  widget: IWidget
  additionalSet: any
  pageWidget: IPageWidget
  additionalSettings?: string
  redactorFromMeta: any
}

export const CustomSettings = (props: IProps) => {
  const updateStyleSettings = (field: Partial<keyof IStyleSettings>, value: string) => {
    const newStyles: IStyleSettings = { ...props.widget.styleSettings, [field]: value }
    props.onChangeField('styleSettings', newStyles)
  }

  const changeSettingsValue = useCallback(
    (field: Partial<keyof IPageWidget>, value: any) => {
      const newPageWidget = { ...props.pageWidget, [field]: value }
      props.updateState('pageWidget', newPageWidget)
    },
    [props.updateState, props.pageWidget]
  )

  return (
    <div>
      <div className={styles.header}>Spacing: </div>
      <div className={styles.line}>
        <SizeSelector
          value={`${parseInt(props.widget.styleSettings?.margins || '0', 10)}`}
          onChange={(value) => {
            updateStyleSettings('margins', `${value}px`)
          }}
          label={'margins'}
        />
        <SizeSelector
          value={`${parseInt(props.widget.styleSettings?.paddings || '0', 10)}`}
          onChange={(value) => {
            updateStyleSettings('paddings', value ? `${value}px` : '')
          }}
          label={'paddings'}
        />
        <SizeSelector
          value={`${parseInt(props.widget.styleSettings?.borderRadius || '0', 10)}`}
          onChange={(value) => {
            updateStyleSettings('borderRadius', `${value}px`)
          }}
          label={'border radius'}
        />
      </div>
      <div>
        <div className={styles.header}>Border: </div>
        <div className={styles.line}>
          <BorderSelector
            editorClassName={styles.editorWidth}
            value={props.widget.styleSettings?.border}
            onChange={(value) => {
              updateStyleSettings('border', value)
            }}
          />
        </div>
      </div>
      <div>
        <div className={styles.header}>Colors: </div>
        <div className={styles.line}>
          <ColorSelectorButton
            className={styles.editorWidth}
            buttonText={'background color'}
            color={props.widget.styleSettings?.backgroundColor}
            onChange={(value) => {
              updateStyleSettings('backgroundColor', value)
            }}
          />
          <ColorSelectorButton
            className={styles.editorWidth}
            buttonText={'header title color'}
            color={props.widget.styleSettings?.headerColor}
            onChange={(value) => {
              updateStyleSettings('headerColor', value)
            }}
          />
        </div>
      </div>
      <div>
        <div>
          <div className={styles.header}>Sizes: </div>
          <div className={styles.line}>
            <HeightSelector
              value={props.pageWidget.height}
              onChange={(value) => {
                if (props.pageWidget) {
                  changeSettingsValue('height', value)
                }
              }}
            />
            {props.pageWidget.height === 'custom' ? (
              <Input
                classes={{ root: styles.editorWidth }}
                label="Custom Height"
                value={`${props.pageWidget.customHeight || ''}`}
                onChange={(value) => {
                  changeSettingsValue('customHeight', parseInt(value) || 0)
                }}
              />
            ) : null}
          </div>
          <div className={styles.line}>
            <Tooltip tipContent={() => <span>Enable content stretching</span>}>
              <Checkbox
                padding
                className={styles.editorWidth}
                label="Can stretch"
                checked={props.pageWidget.canStretch}
                onChange={(value) => {
                  changeSettingsValue('canStretch', value)
                }}
              />
            </Tooltip>
            <Tooltip
              tipContent={() => (
                <span>You can enable a button to show or hide the widget content</span>
              )}
            >
              <Checkbox
                padding
                className={styles.editorWidth}
                label="Toggle button visibility"
                checked={Boolean(props.pageWidget.toggleButtonVisibility)}
                onChange={(value) => {
                  changeSettingsValue('toggleButtonVisibility', value)
                }}
              />
            </Tooltip>
          </div>
        </div>
      </div>
      <AdditionalSettings
        viewModel={props.widget.i}
        settings={props.additionalSettings || '{}'}
        onChange={(newSettings) => props.updateState('additionalSettings', newSettings)}
      />

      <StaticPropsEditor
        viewModel={props.widget.i}
        staticProps={props.pageWidget.staticProps}
        onChange={changeSettingsValue}
      />

      {/* todo: delete? */}
      {/*{!props.redactorFromMeta ? (*/}
      {/*  <div>*/}
      {/*    <div className={styles.line}>*/}
      {/*      <div className={styles.header}>Custom editor</div>*/}
      {/*    </div>*/}
      {/*    <Input*/}
      {/*      className={styles.editors}*/}
      {/*      label="Editor URL"*/}
      {/*      value={props.additionalSet.redactorUrl}*/}
      {/*      onChange={(value: string) => {*/}
      {/*        const newAdditionalSettings = JSON.stringify({*/}
      {/*          ...props.additionalSet,*/}
      {/*          redactorUrl: value,*/}
      {/*        })*/}
      {/*        props.updateState('additionalSettings', newAdditionalSettings)*/}
      {/*      }}*/}
      {/*    />*/}
      {/*  </div>*/}
      {/*) : null}*/}
    </div>
  )
}
